import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import {useTheme} from '@mui/material/styles';
import Container from '@mui/material/Container';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {useAuthContext} from '@context/AuthContext';
import {useMsal, useIsAuthenticated} from '@azure/msal-react';
import {useLocation, useNavigate} from 'react-router-dom';
import {m} from 'framer-motion';

import {useOffSetTop} from 'src/hooks/use-off-set-top';
import {useResponsive} from 'src/hooks/use-responsive';

import {bgBlur} from 'src/theme/css';

import NavMobile from './nav/mobile';
import {HEADER} from '../config-layout';
import {navConfig} from './config-navigation';
import HeaderShadow from '../common/header-shadow';
// import SettingsButton from '../common/settings-button';
import FormConstants from '@shared/constants/FormConstants';
import EncryptUtils from 'src/utils/EncryptUtils';
import AuthUtils from 'src/utils/AuthUtils';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {
  signOutUser,
  adfsLogOutService
} from '@shared/services/service-handlers/PrivateService';
import {BASE_ROUTE} from 'src/routes/BaseRoute';
import {PRIVATE_ROUTES, PUBLIC_ROUTES} from 'src/routes/RouteConstants';
import AccountPopover from '../common/account-popover';
import {Divider, IconButton, MenuItem, Tooltip} from '@mui/material';
import Iconify from 'src/minimal-components/iconify';
import {varHover} from 'src/minimal-components/animate';
import CustomPopover from 'src/minimal-components/custom-popover/custom-popover';
import {usePopover} from 'src/minimal-components/custom-popover';
import {jwtDecode} from 'jwt-decode';

import {OAuthlogoutService} from '@shared/services/service-handlers/PublicService';
import RedEasyButtonComponent from '@components/redEasyButton';
import {environment} from '@shared/config/environment';
import SettingsMenu from './SettingsMenu';

// ----------------------------------------------------------------------

export default function Header(props: any): React.ReactElement {
  const theme = useTheme();
  const popover = usePopover();
  const {helpResourceLinks} = environment;
  const {onHelpOptionClick, setSelectedHelpOption, selectedHelpOption} = props;
  const {IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED} = environment || [];
  let value = false;
  if (IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED === true) {
    value = true;
  }
  const isAzureAuthenticated = useIsAuthenticated();
  const {instance} = useMsal();
  const {t} = useTranslation();
  const {logout} = useAuthContext();
  const {enqueueSnackbar} = useSnackbar();
  const location = useLocation();
  const permissions = AuthUtils.getPermissions();
  const navigate = useNavigate();

  const mdUp = useResponsive('up', 'md');

  const offsetTop = useOffSetTop(HEADER.H_DESKTOP);

  const handleLogOut = async () => {
    try {
      const data: any = {
        sql_data: {
          is_audit: true,
          audit_event_description: FormConstants.COMMON.LOGOUT_SUCCESS
        }
      };

      const userData: any = jwtDecode(AuthUtils.getAuthToken() || '');
      //  return console.log('userData', userData?.user?.type === FormConstants.LOGIN_TYPE.ADFS);
      if (userData?.user?.type === FormConstants.LOGIN_TYPE.OAUTH) {
        const {redirectUrl}: any = await OAuthlogoutService({
          origin: window.location.origin
        });
        AuthUtils.clearStorage();
        window.location.href = redirectUrl;
        return;
      }
      if (userData?.user?.type === FormConstants.LOGIN_TYPE.ADFS) {
        await adfsLogOutService({origin: window.location.origin});
        AuthUtils.clearStorage();
        navigate(`/${BASE_ROUTE}/` + PUBLIC_ROUTES.LOGIN);
        return;
      }

      const encryptRequestData = EncryptUtils.encryptedData(data);
      await signOutUser({
        tk: encryptRequestData
      });

      if (isAzureAuthenticated || AuthUtils.getIsAzureAuthenticated()) {
        AuthUtils.clearStorage();
        navigate(`/${BASE_ROUTE}/` + PUBLIC_ROUTES.LOGIN);
        instance.logoutRedirect({
          postLogoutRedirectUri: '/'
        });
        return;
      } else {
        logout();
      }
      AuthUtils.clearStorage();
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  const helpIconPermission =
    Array.isArray(permissions) &&
    permissions?.some(
      (ele: any) =>
        ele.object === 'settings' && ele.function === 'view-help-icon'
    );

  const handleClickItem = async (url: string) => {
    popover.onClose();
    onHelpOptionClick(url);
  };

  const clearHelpOption = () => {
    setSelectedHelpOption('');
  };

  return (
    <AppBar>
      <Toolbar
        disableGutters
        sx={{
          height: {
            xs: HEADER.H_MOBILE,
            md: HEADER.H_DESKTOP
          },
          transition: theme.transitions.create(['height'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.shorter
          }),
          ...(offsetTop && {
            ...bgBlur({
              color: theme.palette.background.default
            }),
            height: {
              md: HEADER.H_DESKTOP_OFFSET
            }
          }),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
        <Container
          maxWidth={false}
          sx={{height: 1, display: 'flex', alignItems: 'center'}}>
          {/* <Badge
            sx={{
              [`& .${badgeClasses.badge}`]: {
                top: 8,
                right: -16,
                display: 'flex',
                alignItems: 'center',
                position: 'relative'
              }
            }}>
            <Logo />
          </Badge> */}

          <Box sx={{flexGrow: 1}} />

          {/* Header */}
          {/* {mdUp && <NavDesktop data={navConfig} />} */}

          {selectedHelpOption && (
            <Button
              onClick={clearHelpOption}
              variant="outlined"
              sx={{
                marginLeft: 'auto',
                backgroundColor: theme.palette.primary.light
              }}>
              Close Help
            </Button>
          )}

          {!environment?.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED &&
            helpIconPermission &&
            helpResourceLinks?.length > 0 && (
              <Box>
                <Tooltip
                  title={'Help'}
                  placement="bottom"
                  enterDelay={100}
                  leaveDelay={50}>
                  <IconButton
                    component={m.button}
                    whileTap="tap"
                    whileHover="hover"
                    variants={varHover(1.05)}
                    aria-label="help"
                    onClick={popover.onOpen}
                    sx={{
                      width: 50,
                      height: 50
                    }}>
                    <Iconify icon="material-symbols:help" width={50} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          {<SettingsMenu />}
          <CustomPopover
            open={popover.open}
            onClose={popover.onClose}
            sx={{width: 200, p: 0}}>
            <Box sx={{p: 2, pb: 1.5}}>
              <Stack sx={{p: 1}}>
                {helpResourceLinks?.map((option: any) => (
                  <MenuItem
                    component="a"
                    key={option.label}
                    onClick={event => {
                      event.preventDefault();
                      handleClickItem(option.linkTo);
                    }}>
                    {option.label}
                  </MenuItem>
                ))}
              </Stack>
            </Box>
            <Divider sx={{borderStyle: 'dashed'}} />
          </CustomPopover>

          {!environment?.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED && (
            <Stack
              alignItems="center"
              direction={{xs: 'row', md: 'row-reverse'}}>
              {`/${BASE_ROUTE}/${PRIVATE_ROUTES.DASHBOARD}` ===
              location?.pathname ? (
                !value ? (
                  <Button
                    variant="contained"
                    rel="noopener"
                    onClick={handleLogOut}>
                    {t('Sign Out')}
                  </Button>
                ) : (
                  ''
                )
              ) : (
                ''
              )}

              {/* {mdUp && <LoginButton />} */}

              <AccountPopover
                sx={{
                  ml: {xs: 1, md: 0},
                  mr: {md: 2}
                }}
                isDashboard={true}
              />

              {/* <SettingsButton
              sx={{
                ml: {xs: 1, md: 2},
                mr: {md: 1}
              }}
            /> */}

              {!mdUp && <NavMobile data={navConfig} />}
            </Stack>
          )}
          {environment.RED_EASY_BUTTON && (
            <Box sx={{ml: 2}}>
              <RedEasyButtonComponent></RedEasyButtonComponent>
            </Box>
          )}
        </Container>
      </Toolbar>

      {offsetTop && <HeaderShadow />}
    </AppBar>
  );
}

